import { Parse } from '@cadstrom/cadstrom-shared';

import { roundToInterval } from '#utils/round-to-interval.js';

import type { Common, ModelGrid } from '@cadstrom/sigma-engine-shared';

/** Smallest meaningful grid distance, in mm (= 1 µm). */
export const MIN_DISTANCE_MM = 0.001;

export function isValidDistance(distance: number, units: ModelGrid['units']) {
	const converted = Parse.Util.convertGridDistanceUnits(distance, units, 'mm');
	return Number.isFinite(converted) && converted >= MIN_DISTANCE_MM;
}

/**
 * Converts a grid's distance to millimetres, the unit drawing coordinates are in.
 *
 * @param grid - The grid whose distance to convert.
 * @returns The grid distance in millimetres.
 */
function gridDistanceInMm(grid: ModelGrid): number {
	return Parse.Util.convertGridDistanceUnits(grid.distance, grid.units, 'mm');
}

export function toFractionalPrecision(val: number, precision: number, maxFractional = 8): string {
	const valStr = val.toString();
	const [int, fractional] = valStr.split('.', 2);
	const [, precisionFractionalStr] = precision.toString().split('.', 2);
	const precisionFractional = Math.min(precisionFractionalStr?.length ?? 0, maxFractional);
	let retVal: string;
	if (fractional === undefined) {
		retVal = (int ?? '') + '.'.padEnd(precisionFractional + 1, '0');
	} else {
		retVal = `${int}.${fractional.substring(0, precisionFractional).padEnd(precisionFractional, '0')}`;
	}

	if (retVal.endsWith('.')) {
		return retVal.substring(0, retVal.length - 1);
	}

	return retVal;
}

export function maybeInvert(value: number, invert?: boolean) {
	if (invert) {
		return -value;
	}

	return value;
}

export function formatValueForGrid(value: number, grid: ModelGrid, roundToGridIntervals = true) {
	if (isValidDistance(grid.distance, grid.units)) {
		return toFractionalPrecision(
			maybeInvert(
				Parse.Util.convertGridDistanceUnits(
					roundToGridIntervals ? roundToInterval(value, gridDistanceInMm(grid)) : value,
					'mm',
					grid.units,
				),
				grid.invertX,
			),
			grid.distance,
		);
	}

	return undefined;
}

export function formatPositionForGrid(drawingCoords: Common.IPoint, grid: ModelGrid) {
	if (isValidDistance(grid.distance, grid.units)) {
		return {
			x: toFractionalPrecision(
				maybeInvert(
					Parse.Util.convertGridDistanceUnits(
						roundToInterval(drawingCoords.x, gridDistanceInMm(grid)),
						'mm',
						grid.units,
					),
					grid.invertX,
				),
				grid.distance,
			),
			y: toFractionalPrecision(
				maybeInvert(
					Parse.Util.convertGridDistanceUnits(
						roundToInterval(drawingCoords.y, gridDistanceInMm(grid)),
						'mm',
						grid.units,
					),
					grid.invertY,
				),
				grid.distance,
			),
		};
	}

	return undefined;
}
